import { once, isMemoized } from '../lib/once';
import { commissionPreviewKey, draftKey, gateKey, normalizeText } from '../lib/keys';
import { devStore } from '../lib/devlog';
import {
  createCharacter,
  getPricing,
  postGate,
  previewCommission,
  startPreview,
} from '../api/endpoints';
import type {
  CommissionPreview,
  CreateCharacterResult,
  EpisodeLength,
  PreviewStart,
} from '../api/endpoints';
import type { GateVerdict } from '../api/types';
import type { Entry } from './app';

/**
 * Speculation, and its spend guards.
 *
 * The rule that makes this safe is not "be careful": it is that speculation
 * and the real pick call once() with a key from lib/keys.ts, so the second
 * caller gets the first caller's promise. Everything else here is budget.
 *
 * Never speculated: video (~EUR1.20, 30x an image), anything before the gate
 * has returned a canonical (the key would differ from the real one, which IS
 * the double-spend), anything on starter-tile hover (path A has nothing to
 * buy).
 */

const IDLE_MS = 400;
const MIN_CHARS = 3;
const MAX_GATE_CALLS = 6; // per wizard session
const MAX_DRAFT_SPECULATIONS = 2; // ~20c each; the paywall sits before this

let gateCalls = 0;
let draftSpeculations = 0;
let peekSpeculations = 0;
let lastSpeculatedText = '';
let idleTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Characters that have already had their ONE pre-warmed peek.
 *
 * This — not the memo key — is what makes the dithering walk cost one token.
 * See speculatePeek.
 */
const peekedCharacters = new Set<string>();

export function speculationStats() {
  return {
    gateCalls,
    draftSpeculations,
    peekSpeculations,
    maxGate: MAX_GATE_CALLS,
    maxDrafts: MAX_DRAFT_SPECULATIONS,
    /** One pre-warmed peek per character, so this is also the character count. */
    maxPeeksPerCharacter: 1,
  };
}

export function resetSpeculationBudget(): void {
  gateCalls = 0;
  draftSpeculations = 0;
  peekSpeculations = 0;
  lastSpeculatedText = '';
  peekedCharacters.clear();
}

/** The gate, memoized. Both the speculative call and the submit land here. */
export function gate(text: string, speculative: boolean): Promise<GateVerdict> {
  return once(gateKey(text), () => postGate(text, speculative));
}

/**
 * Fire the gate on a typing pause. Conditions, all cheap to check and all
 * load-bearing: >=3 chars, the text GREW since the last speculation (so
 * backspacing does not re-spend), and a per-session ceiling.
 *
 * `currentText` is re-read at fire time, not captured, so a burst of
 * keystrokes produces one call for the final text.
 */
export function scheduleGateSpeculation(
  readCurrentText: () => string,
  onVerdict: (text: string, verdict: GateVerdict) => void,
): void {
  if (idleTimer) clearTimeout(idleTimer);
  if (!devStore.get().speculateGate) return;

  idleTimer = setTimeout(() => {
    const text = readCurrentText();
    const norm = normalizeText(text);
    if (norm.length < MIN_CHARS) return;
    if (norm.length <= lastSpeculatedText.length) return;
    if (isMemoized(gateKey(text))) return;
    if (gateCalls >= MAX_GATE_CALLS) return;

    lastSpeculatedText = norm;
    gateCalls += 1;
    gate(text, true).then(
      (verdict) => onVerdict(text, verdict),
      () => {
        /* memo already evicted; a failed speculation is silent by design */
      },
    );
  }, IDLE_MS);
}

export function cancelGateSpeculation(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
}

/**
 * The character draft, memoized. Path A and path B both come through here, so
 * a speculative draft and the parent's submit can never be two drafts.
 */
export function draft(entry: Entry, speculative: boolean): Promise<CreateCharacterResult> {
  const key = draftKey({ starterSlug: entry.starterSlug, canonical: entry.canonical });
  if (speculative) draftSpeculations += 1;
  return once(key, () =>
    createCharacter({
      starterSlug: entry.starterSlug,
      starterDesc: entry.desc,
      text: entry.starterSlug ? undefined : entry.canonical,
      idempotencyKey: key, // same namespace as the memo — that is the point
    }),
  );
}

/**
 * R5, the stale-speculation guard: an image speculation may only fire from a
 * gate result whose input text still equals what is in the box. The gate
 * result itself stays memoized (it is free); only the ~20c of images is
 * withheld. Speculating from stale text is how the speculative key and the
 * real key drift apart, which is precisely a double spend.
 */
export function maybeSpeculateDraft(
  verdict: GateVerdict,
  gatedText: string,
  currentText: string,
): boolean {
  if (!devStore.get().speculateDrafts) return false;
  if (verdict.verdict !== 'allow') return false;
  if (verdict.catalog) return false; // collapses onto a pre-baked starter: free
  if (!verdict.canonical) return false; // no server identifier = no legal key
  if (normalizeText(gatedText) !== normalizeText(currentText)) return false;
  if (draftSpeculations >= MAX_DRAFT_SPECULATIONS) return false;

  const key = draftKey({ canonical: verdict.canonical });
  if (isMemoized(key)) return false;

  const entry: Entry = { label: verdict.canonical, canonical: verdict.canonical, tier: 'generated' };
  draft(entry, true).catch(() => {
    /* the wizard will retry through the same key when the parent submits */
  });
  return true;
}

/* ------------------------------------------------------------- the free peek
 *
 * The peek is the one speculation that spends something the PARENT can count:
 * a preview token, rendered on the summary as "9 free peeks left". So it gets
 * two guards rather than one, and they answer two different questions.
 *
 *   peekKey()        — "is this exact peek already bought?"  (once(), R3)
 *   peekedCharacters — "has this character already had its one free ride?"
 *
 * The second one is the load-bearing new idea, and it exists because the first
 * one is not enough. A parent who taps tile 2, backs out, taps tile 3, backs
 * out and taps tile 2 again has produced three CHOICES and two distinct keys —
 * so a key-only guard bills two tokens for one moment of indecision, and does
 * it silently. Budgeting the free ride per character makes that walk cost
 * exactly one token however long they dither.
 */

/** The premise a peek runs when POST /commissions/preview cannot plan one.
 *
 *  Shared with Summary rather than duplicated, because the peek's key contains
 *  the request text: two spellings of the same fallback are two cache
 *  partitions and, worse, a pre-warmed peek the summary never adopts. */
export const DEFAULT_PREMISE = 'A gentle first adventure to start things off.';

/** The taster — one 8s segment, what the server plans when no length is sent,
 *  and (today) the first row of GET /pricing. Shared with Summary's FALLBACK
 *  for the same reason as DEFAULT_PREMISE. */
export const DEFAULT_PEEK_SECONDS = 8;

/**
 * Everything the parent is being shown a sketch OF.
 *
 * The candidate index is in here on purpose even though today's server, asked
 * for a peek BEFORE the selection is committed, does not read it (plan.py's
 * chosen_candidate() returns None until characters.py:_select writes
 * chosenCandidate, which needs the name this screen has not collected yet).
 * It is in the identity because it is in what the parent believes they are
 * looking at: switch tiles and the sketch on the summary must not be the one
 * fetched for the tile you walked away from.
 */
export type PeekInput = {
  characterId: string;
  candidateIndex: number;
  request: string;
  seconds: number;
};

/**
 * A peek's spend key.
 *
 * It lives here rather than in lib/keys.ts — which is otherwise "the ONE place
 * a key is derived" — only because nothing outside this module may build one:
 * both call sites (the pre-warm on the candidate screen, the button on the
 * summary) go through peek() and never see a key at all, which is the property
 * lib/keys.ts exists to protect. Move it there next time that file is open.
 *
 * R4 holds: characterId is server-returned, the index is the server's own
 * candidate index, and seconds comes off GET /pricing.
 */
export function peekKey(input: PeekInput): string {
  return [
    'peek',
    input.characterId,
    input.candidateIndex,
    input.seconds,
    normalizeText(input.request),
  ].join(':');
}

/**
 * The free peek, memoized. POST /commissions/peek has NO idempotency key on
 * the server — commissions.py:_peek spends a preview token on every call — so
 * once() is the entire guard. Both the pre-warm and the summary's button land
 * here, and the summary re-entered through the back button lands here again.
 */
export function peek(input: PeekInput, speculative: boolean): Promise<PreviewStart> {
  if (speculative) peekSpeculations += 1;
  return once(peekKey(input), () =>
    startPreview(input.characterId, input.request, input.seconds),
  );
}

/** True when this exact peek is already bought (or being bought right now). */
export function peekStarted(input: PeekInput): boolean {
  return isMemoized(peekKey(input));
}

/** The premise, resolved the same way wherever it is needed. */
export function peekPremise(preview: CommissionPreview | null | undefined): string {
  return preview?.premise?.trim() || DEFAULT_PREMISE;
}

/** The length a peek runs at when the parent has not touched the picker. */
export function peekSeconds(lengths: EpisodeLength[]): number {
  return lengths[0]?.seconds ?? DEFAULT_PEEK_SECONDS;
}

/**
 * Start the free peek the moment the child taps a tile, so it runs while the
 * parent types a name instead of after they have finished.
 *
 * Resolves to whether it fired. Callers do not await it: the wizard must go to
 * the naming screen at tap speed, and a peek that never starts costs the
 * parent nothing but a few seconds of "Sketching the scenes…" later.
 *
 * WHAT IT COSTS AND WHY THAT IS ALLOWED. A peek is a preview token and a
 * handful of ~$0.05 images, and api/commissions.py is emphatic that merely
 * landing on the summary screen must not spend one. This is not that: it fires
 * on a deliberate act — the child choosing a character — once per character,
 * and it is the same token the summary was going to spend anyway a screen
 * later. It rides speculateDrafts because that switch means "do not speculate
 * with image money", and this is image money.
 *
 * WHAT EARLY COSTS US, stated plainly because it is not free. The selection is
 * not committed until the naming screen — characters.py:_select takes the
 * candidate index and the name in ONE write, and there is no rename route, so
 * committing the tile early would lock a name the parent has not typed yet and
 * their real one would 409 away. A peek asked for before that write therefore
 * plans without plan.py's chosen_candidate(): the still is conditioned on the
 * character's portrait rather than the tapped tile's art, and carries no
 * `setting` from the tile's world. So it is a LESS SPECIFIC sketch of the same
 * character in the same scene — never a different pose, because no pose was
 * used — and it lands in a different scene-still partition from the poster the
 * purchased episode will resolve to, which costs one image later.
 *
 * That is the trade for turning several seconds of "Sketching the scenes…" on
 * the spend screen into no wait at all. It stops being a trade the day
 * characters.py can take a candidate index without a name (or can rename): fire
 * the selection here, and the pre-warm becomes byte-for-byte the peek the
 * summary would have asked for.
 */
export async function speculatePeek(
  characterId: string,
  candidateIndex: number,
): Promise<boolean> {
  if (!devStore.get().speculateDrafts) return false;
  if (!characterId) return false;
  // Claimed BEFORE the first await, and never released. Two taps in the same
  // tick — a double-tapped tile, React 18's double-invoked effects — would
  // otherwise both pass the check and both spend. Failing closed here costs a
  // pre-warm; failing open costs a token.
  if (peekedCharacters.has(characterId)) return false;
  peekedCharacters.add(characterId);

  // Both free. The premise comes through the SAME memo the naming and summary
  // screens use, so all three ask for the identical scene — a pre-warm planned
  // from a different premise is a token spent on a sketch the summary will
  // never show.
  const [preview, lengths] = await Promise.all([
    once(commissionPreviewKey(characterId), () => previewCommission(characterId)).catch(
      () => null,
    ),
    getPricing().catch(() => [] as EpisodeLength[]),
  ]);

  const input: PeekInput = {
    characterId,
    candidateIndex,
    request: peekPremise(preview),
    seconds: peekSeconds(lengths),
  };
  if (isMemoized(peekKey(input))) return false;

  peek(input, true).catch(() => {
    // A failed peek is silent by design: the memo evicted itself, so the
    // summary offers the button and the parent spends nothing for our attempt.
    // 402 (no tokens left) arrives here too, and lands the parent on exactly
    // the screen they would have seen without any of this.
  });
  return true;
}
